import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { ConfirmDialog, Select } from '@aeris/ui'

import { setChannelOwner } from '../lib/api/communication-attendance.ts'
import type { ChannelAccountSummaryType } from '../lib/api/communication.ts'
import { fetchSalespeople } from '../lib/api/team.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Quem atende por este número (F210).
 *
 * O dono: "cada um tem o seu WhatsApp". Escolher a Cinthia aqui faz a
 * conversa nova deste número cair em "Minhas" dela — e, confirmado, as
 * abertas que estão sem responsável também. Quem já está atendendo continua.
 */
export const ChannelOwnerPicker = ({
  account,
}: {
  account: ChannelAccountSummaryType
}): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canWrite = allows('communication.queue.write')
  const [pending, setPending] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const people = useQuery({
    queryKey: ['salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
    enabled: allows('sales.write'),
  })
  const nameOf = (userId: string | null): string =>
    userId === null
      ? 'Ninguém'
      : (people.data?.items.find((person) => person.userId === userId)
          ?.displayName ?? 'Alguém da equipe')

  const save = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async (ownerUserId: string | null) =>
      await setChannelOwner(account.id, {
        ownerUserId,
        assignOpen: ownerUserId !== null,
      }),
    onSuccess: async (result) => {
      setPending(null)
      setNotice(
        result.assigned === 0
          ? null
          : `${String(result.assigned)} ${result.assigned === 1 ? 'conversa aberta passou' : 'conversas abertas passaram'} para ${nameOf(result.ownerUserId)}.`
      )
      await queryClient.invalidateQueries({
        queryKey: ['communication-channel-accounts'],
      })
      void queryClient.invalidateQueries({ queryKey: ['attendance-metrics'] })
    },
  })

  if (!canWrite || people.data === undefined) {
    return (
      <span className="text-(length:--text-sm) text-(--text-secondary)">
        {account.ownerUserId === null ? '—' : nameOf(account.ownerUserId)}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <Select
        aria-label={`Quem atende ${account.displayName}`}
        value={account.ownerUserId ?? ''}
        disabled={save.isPending}
        onChange={(event) => {
          const next = event.target.value
          if (next === '') save.mutate(null)
          else setPending(next)
        }}
      >
        <option value="">Ninguém (fica para distribuir)</option>
        {people.data.items.map((person) => (
          <option key={person.userId} value={person.userId}>
            {person.displayName}
          </option>
        ))}
      </Select>
      {notice === null ? null : (
        <span
          role="status"
          className="text-(length:--text-xs) text-(--text-secondary)"
        >
          {notice}
        </span>
      )}
      {save.isError ? (
        <span role="alert" className="text-(length:--text-xs) text-(--danger)">
          Não foi possível salvar agora.
        </span>
      ) : null}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        tone="neutral"
        title={`${nameOf(pending)} atende ${account.displayName}`}
        description={`As conversas novas deste número vão cair em "Minhas" de ${nameOf(pending)}. As abertas que estão sem responsável também passam para ela agora; quem já está atendendo continua.`}
        confirmLabel="Confirmar"
        loading={save.isPending}
        onConfirm={() => {
          save.mutate(pending)
        }}
      />
    </div>
  )
}
